import { AlertTriangle } from 'lucide-react';

export type PayrollBreakdownItem = { code: string; name: string; calculationType: string; configuredValue: string; proratedAmount: string };
export type PayrollLoanDeduction = { loanId: string; loanType: 'loan' | 'advance'; amount: string; balanceBefore: string; balanceAfter: string };
export type PayrollLine = {
  id: string;
  basicAmount: string; proratedBasic: string; totalDaysInMonth: number;
  payableDays: string; lopDays: string; presentDays: string; lateDays: string; halfDays: string;
  onLeavePaidDays: string; onLeaveUnpaidDays: string; holidayDays: string; weekOffCount: string; weekOffWorkedDays: string; weekOffWorkPay: string;
  earningsBreakdown: PayrollBreakdownItem[]; deductionsBreakdown: PayrollBreakdownItem[];
  grossEarnings: string;
  pfEmployeeAmount: string; esiEmployeeAmount: string; ptState: string | null; ptAmount: string; lwfEmployeeAmount: string;
  loanDeductionTotal: string; totalDeductions: string; netPay: string;
  overrideNetPay: string | null; isOverridden: boolean; overrideNote: string | null;
  hasWarning: boolean; warningNote: string | null;
  loanDeductions?: PayrollLoanDeduction[];
};

const money = (v: string) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const componentLabel = (item: PayrollBreakdownItem) => (item.calculationType === 'percent_of_basic' ? `${item.name} (${item.configuredValue}% of basic)` : item.name);

/** The shared earnings/deductions/statutory/loan breakdown — used by both the HR drill-down
 * (any line, any status) and the staff self-view (their own released payslips only). Every
 * percent-of-basic component is labeled with its percentage, never shown as if it were a flat
 * rupee amount — the exact display bug ("5% read as ₹5") that happened once already in Salary. */
export function PayslipBreakdown({ line }: { line: PayrollLine }) {
  const netPay = line.overrideNetPay ?? line.netPay;
  return (
    <div className="space-y-4">
      {line.hasWarning && line.warningNote && (
        <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800"><AlertTriangle size={16} className="mt-0.5 shrink-0" /> {line.warningNote}</p>
      )}
      {line.isOverridden && (
        <p className="rounded-xl bg-canvas px-3 py-2 text-sm text-muted">Net pay manually overridden{line.overrideNote ? `: "${line.overrideNote}"` : ''}. Computed value was {money(line.netPay)}.</p>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl bg-canvas px-3 py-2 text-center"><p className="text-lg font-bold">{line.payableDays}</p><p className="text-xs text-muted">Payable days</p></div>
        <div className="rounded-xl bg-canvas px-3 py-2 text-center"><p className="text-lg font-bold text-red-700">{line.lopDays}</p><p className="text-xs text-muted">LOP days</p></div>
        <div className="rounded-xl bg-canvas px-3 py-2 text-center"><p className="text-lg font-bold">{line.presentDays}</p><p className="text-xs text-muted">Present</p></div>
        <div className="rounded-xl bg-canvas px-3 py-2 text-center"><p className="text-lg font-bold">{line.weekOffCount}</p><p className="text-xs text-muted">Week-offs</p></div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted">Earnings</h4>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between"><span>Basic pay ({money(line.basicAmount)} &times; {Number(line.payableDays)}/{line.totalDaysInMonth} days)</span><span>{money(line.proratedBasic)}</span></div>
          {line.earningsBreakdown.map((item) => <div key={item.code} className="flex justify-between"><span>{componentLabel(item)}</span><span>{money(item.proratedAmount)}</span></div>)}
          {Number(line.weekOffWorkPay) > 0 && <div className="flex justify-between"><span>Week-off work bonus ({line.weekOffWorkedDays} day{line.weekOffWorkedDays === '1.00' ? '' : 's'})</span><span>{money(line.weekOffWorkPay)}</span></div>}
          <div className="flex justify-between border-t border-border pt-1.5 font-semibold"><span>Gross earnings</span><span>{money(line.grossEarnings)}</span></div>
        </div>
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold uppercase tracking-wide text-muted">Deductions</h4>
        <div className="space-y-1.5 text-sm">
          {line.deductionsBreakdown.map((item) => <div key={item.code} className="flex justify-between"><span>{componentLabel(item)}</span><span>{money(item.proratedAmount)}</span></div>)}
          {Number(line.pfEmployeeAmount) > 0 && <div className="flex justify-between"><span>Provident Fund</span><span>{money(line.pfEmployeeAmount)}</span></div>}
          {Number(line.esiEmployeeAmount) > 0 && <div className="flex justify-between"><span>ESI</span><span>{money(line.esiEmployeeAmount)}</span></div>}
          {Number(line.ptAmount) > 0 && <div className="flex justify-between"><span>Professional Tax{line.ptState ? ` (${line.ptState})` : ''}</span><span>{money(line.ptAmount)}</span></div>}
          {Number(line.lwfEmployeeAmount) > 0 && <div className="flex justify-between"><span>Labour Welfare Fund</span><span>{money(line.lwfEmployeeAmount)}</span></div>}
          {Number(line.loanDeductionTotal) > 0 && (
            <div className="flex justify-between">
              <span>Loan / advance instalment{line.loanDeductions && line.loanDeductions.length > 1 ? 's' : ''}</span>
              <span>{money(line.loanDeductionTotal)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-1.5 font-semibold"><span>Total deductions</span><span>{money(line.totalDeductions)}</span></div>
        </div>
      </div>

      <div className="flex justify-between rounded-xl bg-brand-50 px-3 py-3 text-base font-bold text-brand-700"><span>Net pay</span><span>{money(netPay)}</span></div>
    </div>
  );
}
